import { QuickMoveRawEntry } from './QuickMove/QuickMoveEntry';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import {
    AlertTriangle,
    ArrowRight,
    CalendarPlus,
    CalendarRange,
    CheckCircle2,
    Clock3,
    Download,
    Filter,
    Info,
    RotateCcw,
    Save,
    Search,
    Trash2,
    UsersRound,
} from 'lucide-react';
import { colors } from '../../lib/colors';
import { getItemWithExpiration } from '../../lib/utils';
import { formatSessionText, resolveMembershipName } from '../../lib/membershipDisplay';
import { endOfLegacyMondayWeek, startOfLegacyMondayWeek } from './legacyCalendarWeek';
import {
    getScheduleListMaxEndDate,
    isLongScheduleListRange,
    SCHEDULE_LIST_MAX_DATE,
    SCHEDULE_LIST_MIN_DATE,
    validateScheduleListRange,
} from './scheduleListRange';
import {
    createAdminViewPresetAPI,
    deleteAdminViewPresetAPI,
    getAdminViewPresetsAPI,
} from '../../api/adminViewPreset';

type ScheduleListViewProps = {
    viewSwitcher?: React.ReactNode;
    pageType: 'daily' | 'weekly';
    selectedDay: any;
    dailyDataSource: any[] | null;
    weeklyGridData: any[] | null;
    rangeStartDate: string;
    rangeEndDate: string;
    rangeScheduleItems: any[];
    centerId?: string;
    isRangeLoading?: boolean;
    rangeErrorMessage?: string;
    onApplyRange: (startDate: string, endDate: string) => void;
    onRetryRange?: () => void;
    onResetRangeToCurrentView: () => void;
    onClickSchedule: (item: any) => void;
    onClickRequest: (item: any) => void;
    onClickEtcOffSchedule: (item: any) => void;
    onClickNewOpportunity: (item: any) => void;
    onClickApproveScheduleRequest: (item: any) => void;
    onClickRejectScheduleRequest: (item: any) => void;
    onClickCreateSchedule: () => void;
    createTwoButtonAlert?: (
        title: string,
        content: string,
        isError: boolean,
        cancelButtonText?: string,
        confirmButtonText?: string,
        cancelCallBack?: any,
        confirmCallBack?: any,
        isExitButton?: boolean
    ) => void;
};

type ScheduleRowType = 'private' | 'group' | 'request' | 'onetime' | 'block' | 'opportunity';
type ScheduleSortKey = 'start' | 'type' | 'title' | 'coach' | 'membership' | 'status';
type ScheduleSortOrder =
    | 'startAsc'
    | 'startDesc'
    | 'typeAsc'
    | 'typeDesc'
    | 'titleAsc'
    | 'titleDesc'
    | 'coachAsc'
    | 'coachDesc'
    | 'membershipAsc'
    | 'membershipDesc'
    | 'statusAsc'
    | 'statusDesc';

type ScheduleRow = {
    key: string;
    item: any;
    type: ScheduleRowType;
    typeLabel: string;
    typeTone: 'pt' | 'group' | 'request' | 'etc' | 'off' | 'opportunity';
    status: 'upcoming' | 'ongoing' | 'completed' | 'pending' | 'noshow' | 'off';
    statusLabel: string;
    startTime: dayjs.Dayjs;
    endTime: dayjs.Dayjs;
    title: string;
    subTitle: string;
    coachId: string;
    coachName: string;
    memberId: string;
    memberName: string;
    membershipName: string;
    sessionText: string;
    capacityText: string;
    searchText: string;
};

type ScheduleListFilterSnapshot = {
    searchText: string;
    selectedTypeFilters: ScheduleRowType[];
    statusFilter: string;
    coachFilter: string;
    memberFilter: string;
    sortOrder: ScheduleSortOrder;
    rangeStartDate: string;
    rangeEndDate: string;
};

type SavedScheduleView = {
    id: string;
    name: string;
    filters: ScheduleListFilterSnapshot;
    updatedAt: string;
};

const CLASS_TYPE_LABEL: Record<string, string> = {
    GROUP: '그룹',
    PRIVATE_1ON2: '1:2',
    PRIVATE_1ON3: '1:3',
};

const DEFAULT_TYPE_FILTERS: ScheduleRowType[] = ['private', 'group', 'onetime', 'opportunity'];
const DEFAULT_SORT_ORDER: ScheduleSortOrder = 'startAsc';
const SCHEDULE_LIST_VIEW_TYPE = 'schedule-list';
const SCHEDULE_LIST_FILTER_STORAGE_KEY = 'rappo:schedule-list-filters:v1';
const SCHEDULE_LIST_SAVED_VIEW_STORAGE_KEY = 'rappo:schedule-list-saved-views:v1';
const SCHEDULE_LIST_SAVED_VIEW_MIGRATION_STORAGE_KEY = 'rappo:schedule-list-saved-views-migrated:v1';
const LOCAL_SAVED_VIEW_ID_PREFIX = 'schedule-view-';

const TYPE_FILTER_OPTIONS: Array<{ value: ScheduleRowType; label: string }> = [
    { value: 'private', label: 'PT' },
    { value: 'group', label: '그룹수업' },
    { value: 'onetime', label: '일반일정' },
    { value: 'opportunity', label: '신규 기회' },
    { value: 'request', label: '신청 대기' },
    { value: 'block', label: 'OFF' },
];

const VALID_TYPE_FILTERS = new Set<ScheduleRowType>(TYPE_FILTER_OPTIONS.map((option) => option.value));

const SORT_OPTIONS: Array<{ value: ScheduleSortOrder; label: string }> = [
    { value: 'startAsc', label: '시간 빠른순' },
    { value: 'startDesc', label: '시간 늦은순' },
    { value: 'typeAsc', label: '유형 가나다순' },
    { value: 'typeDesc', label: '유형 역순' },
    { value: 'titleAsc', label: '수업명 가나다순' },
    { value: 'titleDesc', label: '수업명 역순' },
    { value: 'coachAsc', label: '강사명 가나다순' },
    { value: 'coachDesc', label: '강사명 역순' },
    { value: 'membershipAsc', label: '수강권명 가나다순' },
    { value: 'membershipDesc', label: '수강권명 역순' },
    { value: 'statusAsc', label: '상태순' },
    { value: 'statusDesc', label: '상태 역순' },
];

const STATUS_FILTER_LABELS: Record<string, string> = {
    upcoming: '예정',
    ongoing: '진행중',
    completed: '완료',
    pending: '신청 대기',
    noshow: '노쇼',
    off: 'OFF',
};

const STATUS_SORT_WEIGHT: Record<ScheduleRow['status'], number> = {
    upcoming: 1,
    ongoing: 2,
    completed: 3,
    pending: 4,
    noshow: 5,
    off: 6,
};

const getScheduleStorageKey = (baseKey: string, centerId?: string) => `${baseKey}:${centerId || 'default'}`;

const readStorageJson = <T,>(key: string, fallback: T): T => {
    try {
        const rawValue = window.localStorage.getItem(key);
        if (!rawValue) return fallback;
        return JSON.parse(rawValue) as T;
    } catch {
        return fallback;
    }
};

const writeStorageJson = (key: string, value: unknown) => {
    try {
        window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
        // Storage failures should not block schedule operations.
    }
};

const normalizeTypeFilters = (value: unknown, fallback: ScheduleRowType[] = DEFAULT_TYPE_FILTERS) => {
    if (!Array.isArray(value)) return fallback;
    return value.filter((type): type is ScheduleRowType => VALID_TYPE_FILTERS.has(type as ScheduleRowType));
};

const normalizeSortOrder = (value: unknown): ScheduleSortOrder => {
    return SORT_OPTIONS.some((option) => option.value === value) ? (value as ScheduleSortOrder) : DEFAULT_SORT_ORDER;
};

const normalizeFilterSnapshot = (value: any): ScheduleListFilterSnapshot | null => {
    if (!value || typeof value !== 'object') return null;
    return {
        searchText: typeof value.searchText === 'string' ? value.searchText : '',
        selectedTypeFilters: normalizeTypeFilters(value.selectedTypeFilters),
        statusFilter: typeof value.statusFilter === 'string' ? value.statusFilter : 'all',
        coachFilter: typeof value.coachFilter === 'string' ? value.coachFilter : 'all',
        memberFilter: typeof value.memberFilter === 'string' ? value.memberFilter : 'all',
        sortOrder: normalizeSortOrder(value.sortOrder),
        rangeStartDate: typeof value.rangeStartDate === 'string' ? value.rangeStartDate : '',
        rangeEndDate: typeof value.rangeEndDate === 'string' ? value.rangeEndDate : '',
    };
};

const normalizeSavedViews = (value: unknown): SavedScheduleView[] => {
    if (!Array.isArray(value)) return [];
    return value
        .map((view) => {
            if (!view || typeof view !== 'object') return null;
            const rawView = view as any;
            const filters = normalizeFilterSnapshot(rawView.filters);
            if (!filters || typeof rawView.id !== 'string' || typeof rawView.name !== 'string') return null;
            return {
                id: rawView.id,
                name: rawView.name,
                filters,
                updatedAt: typeof rawView.updatedAt === 'string' ? rawView.updatedAt : '',
            };
        })
        .filter((view): view is SavedScheduleView => Boolean(view));
};

const getComparableFilterSnapshot = (filters: ScheduleListFilterSnapshot) =>
    JSON.stringify({
        ...filters,
        selectedTypeFilters: [...filters.selectedTypeFilters].sort(),
    });

const areFilterSnapshotsEqual = (left: ScheduleListFilterSnapshot, right: ScheduleListFilterSnapshot) =>
    getComparableFilterSnapshot(left) === getComparableFilterSnapshot(right);

const normalizeApiSavedView = (value: any): SavedScheduleView | null => {
    if (!value || typeof value !== 'object') return null;
    const filters = normalizeFilterSnapshot(value.filters);
    const id = typeof value.id === 'string' ? value.id : typeof value._id === 'string' ? value._id : '';
    if (!id || !filters || typeof value.name !== 'string') return null;
    return {
        id,
        name: value.name,
        filters,
        updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : '',
    };
};

const getSavedViewFingerprint = (view: SavedScheduleView) =>
    `${view.name}::${getComparableFilterSnapshot(view.filters)}`;

const mergeSavedViews = (primaryViews: SavedScheduleView[], fallbackViews: SavedScheduleView[] = []) => {
    const result: SavedScheduleView[] = [];
    const seen = new Set<string>();

    [...primaryViews, ...fallbackViews].forEach((view) => {
        const fingerprint = getSavedViewFingerprint(view);
        if (seen.has(fingerprint)) return;
        seen.add(fingerprint);
        result.push(view);
    });

    return result;
};

const isLocalSavedViewId = (id: string) => id.startsWith(LOCAL_SAVED_VIEW_ID_PREFIX);

const areTypeFiltersEqual = (left: ScheduleRowType[], right: ScheduleRowType[]) =>
    left.length === right.length && left.every((type) => right.includes(type));

const PageWrap = styled.div`
    width: calc(100% - 8.88%);
    margin: 16px 4.44% 0;
    padding-bottom: 40px;
`;

const Toolbar = styled.div`
    min-height: 76px;
    padding: 18px 20px;
    border: 1px solid #dbe6f5;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 8px 22px rgba(23, 38, 74, 0.045);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
`;

const SummaryGrid = styled.div`
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(6, minmax(120px, 1fr));
    gap: 10px;
`;

const SummaryCard = styled.div`
    min-height: 66px;
    border: 1px solid #dfe8f6;
    border-radius: 10px;
    background: #ffffff;
    padding: 12px 14px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
`;

const FilterRow = styled.div`
    margin-top: 12px;
    padding: 16px 18px;
    border: 1px solid #dbe6f5;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 8px 22px rgba(23, 38, 74, 0.04);
    display: flex;
    flex-direction: column;
    gap: 14px;
`;

const FilterTopRow = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
    flex-wrap: wrap;
`;

const TopRightControls = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-wrap: wrap;
`;

const FilterBottomRow = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 18px;
    min-width: 0;
    flex-wrap: wrap;
    padding-top: 2px;
`;

const FilterGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 7px;
    min-width: 0;
`;

const FilterLabel = styled.span`
    color: #6b778c;
    font-size: 12px;
    font-weight: 900;
    line-height: 16px;
    white-space: nowrap;
`;

const SelectGroup = styled.div`
    display: flex;
    align-items: flex-end;
    gap: 10px;
    flex-wrap: wrap;
`;

const SelectControl = styled.label`
    display: flex;
    flex-direction: column;
    gap: 7px;
`;

const CountInfo = styled.div`
    flex: 0 0 auto;
    min-width: 132px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid #e0e8f5;
    border-radius: 10px;
    background: #f8fbff;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    color: #8490a3;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
`;

const ActiveFilterBar = styled.div`
    display: flex;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
    min-height: 28px;
`;

const ActiveFilterChip = styled.button`
    height: 28px;
    padding: 0 9px;
    border-radius: 999px;
    border: 1px solid #cfe0ff;
    background: #f6f9ff;
    color: #3e5f99;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 850;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: #edf4ff;
        border-color: #a9c5ff;
        color: ${colors.BLUE_500};
    }
`;

const SearchBox = styled.label`
    width: 360px;
    flex: 0 0 360px;
    height: 44px;
    border: 1px solid #d6e1f2;
    border-radius: 10px;
    background: #fdfeff;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 14px;
    color: #8a96aa;
`;

const SearchInput = styled.input`
    width: 100%;
    border: none;
    outline: none;
    color: #17264a;
    font-size: 14px;
    font-weight: 600;

    &::placeholder {
        color: #a6b0c1;
    }
`;

const DateRangePanel = styled.section`
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #cfe0ff;
    border-radius: 12px;
    background: linear-gradient(135deg, #f8fbff 0%, #ffffff 72%);
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const DateRangeHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
`;

const DateRangeTitleGroup = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 9px;
    min-width: 260px;
    color: ${colors.BLUE_500};
`;

const DateRangeTitle = styled.div`
    color: #17264a;
    font-size: 14px;
    font-weight: 900;
    line-height: 18px;
`;

const DateRangeSubtitle = styled.div`
    margin-top: 3px;
    color: #6b778c;
    font-size: 12px;
    font-weight: 700;
    line-height: 17px;
`;

const AppliedRangeBadge = styled.div`
    min-height: 32px;
    padding: 6px 10px;
    border: 1px solid #cfe0ff;
    border-radius: 999px;
    background: #ffffff;
    color: #3e5f99;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 850;
    line-height: 18px;
`;

const DateRangeControls = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
`;

const WeekPresetGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const DateEditor = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
    gap: 8px;
    flex-wrap: wrap;
`;

const DateField = styled.label`
    display: flex;
    flex-direction: column;
    gap: 5px;
`;

const DateFieldLabel = styled.span`
    color: #68758a;
    font-size: 11px;
    font-weight: 900;
    line-height: 14px;
`;

const DateInput = styled.input<{ $invalid?: boolean }>`
    width: 142px;
    height: 38px;
    border: 1px solid ${({ $invalid }) => ($invalid ? '#ef9a9a' : '#cbd9ed')};
    border-radius: 8px;
    padding: 0 9px;
    background: ${({ $invalid }) => ($invalid ? '#fff8f8' : '#ffffff')};
    color: #17264a;
    font-size: 13px;
    font-weight: 800;
    outline: none;

    &:focus {
        border-color: ${({ $invalid }) => ($invalid ? '#dc2626' : colors.BLUE_500)};
        box-shadow: 0 0 0 3px ${({ $invalid }) => ($invalid ? 'rgba(220, 38, 38, 0.10)' : 'rgba(61, 115, 221, 0.12)')};
    }
`;

const RangeButton = styled.button<{ $selected?: boolean }>`
    height: 34px;
    padding: 0 11px;
    border-radius: 8px;
    border: 1px solid ${({ $selected }) => ($selected ? '#8FB7FF' : '#D8E2F1')};
    background: ${({ $selected }) => ($selected ? '#EFF6FF' : '#FFFFFF')};
    color: ${({ $selected }) => ($selected ? colors.BLUE_500 : '#6B778C')};
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        background: ${({ $selected }) => ($selected ? '#E6F0FF' : '#F8FBFF')};
        border-color: #9fbae4;
    }
`;

const ApplyRangeButton = styled.button`
    height: 38px;
    min-width: 98px;
    padding: 0 15px;
    border: 1px solid ${colors.BLUE_500};
    border-radius: 9px;
    background: ${colors.BLUE_500};
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 900;
    cursor: pointer;
    box-shadow: 0 7px 16px rgba(61, 115, 221, 0.18);

    &:hover:not(:disabled) {
        background: #2f66d0;
        border-color: #2f66d0;
    }

    &:disabled {
        cursor: not-allowed;
        border-color: #cbd5e1;
        background: #e7ecf3;
        color: #94a0b2;
        box-shadow: none;
    }
`;

type RangeFeedbackTone = 'error' | 'warning' | 'pending' | 'info';

const RangeFeedback = styled.div<{ $tone: RangeFeedbackTone }>`
    min-height: 34px;
    padding: 7px 10px;
    border: 1px solid
        ${({ $tone }) =>
            $tone === 'error'
                ? '#fecaca'
                : $tone === 'warning'
                ? '#fed7aa'
                : $tone === 'pending'
                ? '#bfdbfe'
                : '#dbe6f5'};
    border-radius: 9px;
    background: ${({ $tone }) =>
        $tone === 'error'
            ? '#fff7f7'
            : $tone === 'warning'
            ? '#fffaf2'
            : $tone === 'pending'
            ? '#f5f9ff'
            : '#ffffff'};
    color: ${({ $tone }) =>
        $tone === 'error'
            ? '#b91c1c'
            : $tone === 'warning'
            ? '#9a5a0a'
            : $tone === 'pending'
            ? '#2f66d0'
            : '#68758a'};
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 800;
    line-height: 18px;
`;

const ResetFilterButton = styled.button<{ $active?: boolean }>`
    height: 40px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid ${({ $active }) => ($active ? '#8fb7ff' : '#d8e2f1')};
    background: ${({ $active }) => ($active ? '#eff6ff' : '#ffffff')};
    color: ${({ $active }) => ($active ? colors.BLUE_500 : '#6b778c')};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;
    box-shadow: ${({ $active }) => ($active ? '0 6px 14px rgba(47, 111, 237, 0.12)' : 'none')};

    &:hover {
        background: ${({ $active }) => ($active ? '#e6f0ff' : '#f8fbff')};
        color: ${({ $active }) => ($active ? colors.BLUE_500 : '#3e4b5f')};
        border-color: ${({ $active }) => ($active ? '#6fa2ff' : '#bfd0ea')};
    }
`;

const TypeFilterGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
`;

const TypeFilterButton = styled.button<{ $selected: boolean }>`
    height: 34px;
    padding: 0 12px;
    border-radius: 999px;
    border: 1px solid ${({ $selected }) => ($selected ? '#8FB7FF' : '#D8E2F1')};
    background: ${({ $selected }) => ($selected ? '#EFF6FF' : '#FFFFFF')};
    color: ${({ $selected }) => ($selected ? colors.BLUE_500 : '#6B778C')};
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        border-color: ${({ $selected }) => ($selected ? '#6D9CF2' : '#BFD0EA')};
        background: ${({ $selected }) => ($selected ? '#E6F0FF' : '#F8FBFF')};
        color: ${({ $selected }) => ($selected ? '#2F66D0' : '#3E4B5F')};
    }
`;

const Select = styled.select`
    width: 150px;
    height: 40px;
    border: 1px solid #d6e1f2;
    border-radius: 10px;
    background: #ffffff;
    color: #17264a;
    font-size: 14px;
    font-weight: 700;
    padding: 0 12px;
    outline: none;
`;

const CreateButton = styled.button`
    height: 42px;
    padding: 0 16px;
    border: none;
    border-radius: 10px;
    background: ${colors.BLUE_500};
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
    box-shadow: 0 8px 18px rgba(61, 115, 221, 0.18);

    &:hover {
        background: #2f66d0;
    }
`;

const ToolbarActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 0 0 auto;
`;

const SecondaryActionButton = styled.button`
    height: 40px;
    padding: 0 13px;
    border-radius: 10px;
    border: 1px solid #d6e1f2;
    background: #ffffff;
    color: #3e4b5f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 850;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: #f8fbff;
        border-color: #bfd0ea;
        color: ${colors.BLUE_500};
    }

    &:disabled {
        cursor: not-allowed;
        color: #a6b0c1;
        background: #f8fafc;
    }
`;

const FavoriteBar = styled.div`
    min-height: 40px;
    padding: 9px 12px;
    border: 1px solid #cfe0ff;
    border-radius: 10px;
    background: #f7faff;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    flex-wrap: wrap;
`;

const FavoriteControls = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const FavoritePrompt = styled.div`
    min-width: 240px;
    color: #536780;
    font-size: 13px;
    font-weight: 800;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const FavoriteSelect = styled.select`
    width: 180px;
    height: 36px;
    border: 1px solid #d6e1f2;
    border-radius: 9px;
    background: #ffffff;
    color: #17264a;
    font-size: 13px;
    font-weight: 800;
    padding: 0 10px;
    outline: none;
`;

const FavoriteNameInput = styled.input`
    width: 190px;
    height: 36px;
    border: 1px solid #d6e1f2;
    border-radius: 9px;
    background: #ffffff;
    color: #17264a;
    font-size: 13px;
    font-weight: 700;
    padding: 0 11px;
    outline: none;

    &::placeholder {
        color: #a6b0c1;
    }
`;

const FavoriteButton = styled.button`
    height: 32px;
    padding: 0 10px;
    border-radius: 9px;
    border: 1px solid #d6e1f2;
    background: #ffffff;
    color: #3e4b5f;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 850;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) {
        background: #eff6ff;
        color: ${colors.BLUE_500};
        border-color: #8fb7ff;
    }

    &:disabled {
        cursor: not-allowed;
        color: #a6b0c1;
        background: #f8fafc;
    }
`;

const FavoritePrimaryButton = styled(FavoriteButton)`
    border-color: #a9c5ff;
    background: #ffffff;
    color: ${colors.BLUE_500};
    box-shadow: 0 3px 8px rgba(61, 115, 221, 0.08);

    &:hover:not(:disabled) {
        background: #e6f0ff;
        color: #2f66d0;
        border-color: #6d9cf2;
    }
`;

const FavoriteIconButton = styled(FavoriteButton)`
    width: 36px;
    padding: 0;
    color: #dc2626;

    &:hover:not(:disabled) {
        background: #fef2f2;
        color: #dc2626;
        border-color: #fecaca;
    }
`;

const TableShell = styled.div`
    margin-top: 12px;
    border: 1px solid #dce6f8;
    border-radius: 14px;
    background: #ffffff;
    box-shadow: 0 10px 30px rgba(23, 38, 74, 0.06);
    overflow: hidden;
`;

const TableScroll = styled.div`
    overflow-x: auto;
`;

const GridRow = styled.div<{ $header?: boolean }>`
    min-width: 1180px;
    display: grid;
    grid-template-columns: 128px 136px 116px minmax(230px, 1.4fr) 150px minmax(190px, 1fr) 124px 132px;
    align-items: center;
    column-gap: 14px;
    min-height: ${({ $header }) => ($header ? '48px' : '68px')};
    padding: 0 18px;
    border-bottom: 1px solid #edf2fa;
    background: ${({ $header }) => ($header ? '#f7faff' : '#ffffff')};
    color: ${({ $header }) => ($header ? '#627089' : '#17264a')};
    font-size: ${({ $header }) => ($header ? '13px' : '14px')};
    font-weight: ${({ $header }) => ($header ? 800 : 700)};

    &:last-child {
        border-bottom: none;
    }

    ${({ $header }) =>
        !$header &&
        `
        cursor: pointer;
        transition: background 0.12s ease, box-shadow 0.12s ease;
        &:hover {
            background: #f8fbff;
            box-shadow: inset 3px 0 0 ${colors.BLUE_500};
        }

        &:focus-visible {
            outline: none;
            box-shadow: inset 0 0 0 2px #8fb7ff;
        }
    `}
`;

const CellText = styled.div`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const CenterCell = styled.div`
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const HeaderLabel = styled.span`
    font-weight: 900;
    color: #627089;
    white-space: nowrap;
`;

const SortHeaderButton = styled.button<{ $active?: boolean; $center?: boolean }>`
    width: fit-content;
    max-width: 100%;
    border: none;
    background: transparent;
    color: ${({ $active }) => ($active ? colors.BLUE_500 : '#627089')};
    display: inline-flex;
    align-items: center;
    justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
    gap: 5px;
    padding: 0;
    font: inherit;
    font-weight: 900;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
        color: ${colors.BLUE_500};
    }
`;

const SortIndicator = styled.span<{ $active?: boolean }>`
    color: ${({ $active }) => ($active ? colors.BLUE_500 : '#a5b1c4')};
    font-size: 10px;
    line-height: 1;
`;

const ActionCell = styled(CenterCell)`
    gap: 6px;
`;

const SubText = styled.div`
    min-width: 0;
    margin-top: 4px;
    color: #8490a3;
    font-size: 12px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const TypeBadge = styled.span<{ $tone: ScheduleRow['typeTone'] }>`
    width: fit-content;
    max-width: 108px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 900;
    color: ${({ $tone }) =>
        $tone === 'group'
            ? '#065F46'
            : $tone === 'request'
            ? '#C2410C'
            : $tone === 'etc'
            ? '#374151'
            : $tone === 'off'
            ? '#64748B'
            : $tone === 'opportunity'
            ? '#B45309'
            : '#1D4ED8'};
    background: ${({ $tone }) =>
        $tone === 'group'
            ? '#ECFDF5'
            : $tone === 'request'
            ? '#FFF7ED'
            : $tone === 'etc'
            ? '#F3F4F6'
            : $tone === 'off'
            ? '#F1F5F9'
            : $tone === 'opportunity'
            ? '#FFFBEB'
            : '#EFF6FF'};
    border: 1px solid
        ${({ $tone }) =>
            $tone === 'group'
                ? '#A7F3D0'
                : $tone === 'request'
                ? '#FED7AA'
                : $tone === 'etc'
                ? '#E5E7EB'
                : $tone === 'off'
                ? '#E2E8F0'
                : $tone === 'opportunity'
                ? '#FDE68A'
                : '#BFDBFE'};
`;

const StatusBadge = styled.span<{ $status: ScheduleRow['status'] }>`
    width: fit-content;
    min-width: 58px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 900;
    color: ${({ $status }) =>
        $status === 'ongoing'
            ? '#15803D'
            : $status === 'completed'
            ? '#475569'
            : $status === 'pending'
            ? '#C2410C'
            : $status === 'noshow'
            ? '#DC2626'
            : $status === 'off'
            ? '#64748B'
            : '#1D4ED8'};
    background: ${({ $status }) =>
        $status === 'ongoing'
            ? '#DCFCE7'
            : $status === 'completed'
            ? '#F1F5F9'
            : $status === 'pending'
            ? '#FFF7ED'
            : $status === 'noshow'
            ? '#FEF2F2'
            : $status === 'off'
            ? '#F8FAFC'
            : '#DBEAFE'};
`;

const EmptyState = styled.div`
    min-height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
    color: #8490a3;
    font-size: 14px;
    font-weight: 700;
`;

const InlineButton = styled.button<{ $variant?: 'primary' | 'danger' }>`
    height: 32px;
    padding: 0 11px;
    border-radius: 8px;
    border: 1px solid ${({ $variant }) => ($variant === 'danger' ? '#FECACA' : '#C9D8F8')};
    background: ${({ $variant }) => ($variant === 'primary' ? '#EFF6FF' : '#FFFFFF')};
    color: ${({ $variant }) => ($variant === 'danger' ? '#DC2626' : '#2F66D0')};
    font-size: 12px;
    font-weight: 800;
    cursor: pointer;

    &:hover {
        background: ${({ $variant }) => ($variant === 'danger' ? '#FEF2F2' : '#F8FBFF')};
    }
`;

const getScheduleId = (item: any, index: number) =>
    item?._id ||
    item?.id ||
    item?.newOpportunityId ||
    item?.blockId ||
    `${item?.type || 'schedule'}-${item?.coachId || ''}-${item?.startTime || ''}-${item?.endTime || ''}-${item?.alias || item?.name || index}`;

const isGroupClassSchedule = (item: any) => item?.classType && item.classType !== 'PRIVATE_1ON1';

const getDedupeKey = (item: any, index: number) => {
    if (isGroupClassSchedule(item)) {
        return `group-${item?._id || item?.id || `${item?.coachId}-${item?.startTime}-${item?.endTime}-${item?.classType}`}`;
    }
    return `${item?._id || item?.id || item?.newOpportunityId || item?.blockId || index}-${item?.type}-${item?.coachId}-${item?.startTime}-${item?.endTime}-${item?.alias || item?.name || item?.title || ''}`;
};

const getStatus = (item: any): { status: ScheduleRow['status']; label: string } => {
    if (item?.type === 'request') return { status: 'pending', label: item?.requestType === 'change' ? '변경 요청' : '신청 대기' };
    if (item?.type === 'block') return { status: 'off', label: 'OFF' };
    if (item?.attendanceStatus === 'NOSHOW') return { status: 'noshow', label: '노쇼' };
    if (dayjs().isAfter(item?.startTime) && dayjs().isBefore(item?.endTime)) return { status: 'ongoing', label: '진행중' };
    if (dayjs().isAfter(item?.endTime)) return { status: 'completed', label: '완료' };
    return { status: 'upcoming', label: '예정' };
};

const getTypeInfo = (item: any): { type: ScheduleRowType; label: string; tone: ScheduleRow['typeTone'] } => {
    if (item?.type === 'request') return { type: 'request', label: item?.requestType === 'change' ? '변경 요청' : '신청 대기', tone: 'request' };
    if (item?.type === 'onetimeSchedule') return { type: 'onetime', label: '일반', tone: 'etc' };
    if (item?.type === 'block') return { type: 'block', label: 'OFF', tone: 'off' };
    if (item?.type === 'newOpportunity') return { type: 'opportunity', label: '신규기회', tone: 'opportunity' };
    if (isGroupClassSchedule(item)) {
        return { type: 'group', label: CLASS_TYPE_LABEL[item.classType] || '그룹', tone: 'group' };
    }
    return { type: 'private', label: 'PT', tone: 'pt' };
};

const getMemberName = (item: any) => {
    if (item?.type === 'newOpportunity') return item?.memberName || item?.alias || '';
    if (item?.type === 'schedule' && !isGroupClassSchedule(item)) return item?.alias || item?.memberName || '';
    if (item?.type === 'request') return item?.alias || item?.memberName || '';
    return item?.member?.name || '';
};

const toScheduleRow = (item: any, index: number): ScheduleRow | null => {
    if (!item?.startTime || !item?.endTime) return null;
    const typeInfo = getTypeInfo(item);
    const statusInfo = getStatus(item);
    const memberName = getMemberName(item);
    const title =
        item?.type === 'newOpportunity'
            ? item?.title || item?.categoryName || '신규기회'
            : item?.type === 'onetimeSchedule'
            ? item?.name || '일반 일정'
            : item?.type === 'block'
            ? 'OFF'
            : item?.title || item?.name || item?.alias || '수업';
    const subTitle =
        item?.type === 'schedule' && isGroupClassSchedule(item)
            ? ''
            : item?.type === 'schedule'
            ? item?.alias
                ? `${item.alias} 회원`
                : ''
            : item?.type === 'request'
            ? item?.alias
                ? `${item.alias} 회원`
                : ''
            : item?.type === 'newOpportunity'
            ? item?.memberName || item?.alias || ''
            : item?.memo || '';
    const membershipName = resolveMembershipName(item);
    const sessionText =
        item?.type === 'schedule' && !isGroupClassSchedule(item)
            ? `${formatSessionText(item, '?/?')}회`
            : item?.type === 'schedule' && isGroupClassSchedule(item)
            ? `${item?.currentCount ?? 0}/${item?.maxCapacity ?? '?'}명`
            : '';
    const capacityText = isGroupClassSchedule(item) ? `${item?.currentCount ?? 0}/${item?.maxCapacity ?? '?'}명` : '';
    const coachId = item?.coachId || '';
    const coachName = item?.coachName || '';
    const memberId = item?.memberId || item?.member?._id || item?.member?.id || '';
    const startTime = dayjs(item.startTime);
    const endTime = dayjs(item.endTime);

    return {
        key: `${getScheduleId(item, index)}-${index}`,
        item,
        type: typeInfo.type,
        typeLabel: typeInfo.label,
        typeTone: typeInfo.tone,
        status: statusInfo.status,
        statusLabel: statusInfo.label,
        startTime,
        endTime,
        title,
        subTitle,
        coachId,
        coachName,
        memberId,
        memberName,
        membershipName,
        sessionText,
        capacityText,
        searchText: [title, subTitle, coachName, memberName, membershipName, item?.categoryName, item?.memberName]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
    };
};

const flattenDailyRows = (dailyDataSource: any[] | null, selectedDay: any) => {
    const rows: ScheduleRow[] = [];
    const seen = new Set<string>();
    (dailyDataSource || []).forEach((coach: any) => {
        (coach?.schedules || []).forEach((schedule: any, index: number) => {
            if (!dayjs(schedule?.startTime).isSame(dayjs(selectedDay), 'day')) return;
            const enrichedSchedule = {
                ...schedule,
                coachId: schedule?.coachId || coach?.coachId,
                coachName: schedule?.coachName || coach?.coachName,
                coachProfileImageUrl: schedule?.coachProfileImageUrl || coach?.coachProfileImageUrl,
            };
            const dedupeKey = getDedupeKey(enrichedSchedule, rows.length + index);
            if (seen.has(dedupeKey)) return;
            seen.add(dedupeKey);
            const row = toScheduleRow(enrichedSchedule, rows.length + index);
            if (row) rows.push(row);
        });
    });
    return rows;
};

const flattenWeeklyRows = (weeklyGridData: any[] | null) => {
    const rows: ScheduleRow[] = [];
    const seen = new Set<string>();
    (weeklyGridData || []).forEach((hourRow: any[]) => {
        (hourRow || []).forEach((cell: any, cellIndex: number) => {
            if (cellIndex === 0 || !Array.isArray(cell)) return;
            cell.forEach((schedule: any) => {
                const key = getDedupeKey(schedule, rows.length);
                if (seen.has(key)) return;
                seen.add(key);
                const row = toScheduleRow(schedule, rows.length);
                if (row) rows.push(row);
            });
        });
    });
    return rows;
};

const flattenScheduleItems = (scheduleItems: any[], rangeStartDate: string, rangeEndDate: string) => {
    const rows: ScheduleRow[] = [];
    const seen = new Set<string>();
    const rangeStart = dayjs(rangeStartDate).startOf('day');
    const rangeEnd = dayjs(rangeEndDate).endOf('day');

    scheduleItems.forEach((schedule: any, index: number) => {
        const scheduleStart = dayjs(schedule?.startTime);
        if (!scheduleStart.isValid() || scheduleStart.isBefore(rangeStart) || scheduleStart.isAfter(rangeEnd)) return;
        const key = getDedupeKey(schedule, index);
        if (seen.has(key)) return;
        seen.add(key);
        const row = toScheduleRow(schedule, index);
        if (row) rows.push(row);
    });
    return rows;
};

const getRangeLabel = (rangeStartDate: string, rangeEndDate: string) => {
    if (dayjs(rangeStartDate).isSame(dayjs(rangeEndDate), 'day')) {
        return dayjs(rangeStartDate).format('YYYY년 M월 D일 (ddd)');
    }
    return `${dayjs(rangeStartDate).format('YYYY년 M월 D일')} ~ ${dayjs(rangeEndDate).format('YYYY년 M월 D일')}`;
};

const isSameRange = (startDate: string, endDate: string, expectedStartDate: string, expectedEndDate: string) =>
    startDate === expectedStartDate && endDate === expectedEndDate;

const getSortLabel = (sortOrder: ScheduleSortOrder) =>
    SORT_OPTIONS.find((option) => option.value === sortOrder)?.label || SORT_OPTIONS[0].label;

const getSortConfig = (sortOrder: ScheduleSortOrder): { key: ScheduleSortKey; direction: 'asc' | 'desc' } => {
    const direction = sortOrder.endsWith('Desc') ? 'desc' : 'asc';
    const rawKey = sortOrder.replace(/Asc|Desc/, '') as ScheduleSortKey;
    return { key: rawKey, direction };
};

const toSortOrder = (key: ScheduleSortKey, direction: 'asc' | 'desc'): ScheduleSortOrder =>
    `${key}${direction === 'asc' ? 'Asc' : 'Desc'}` as ScheduleSortOrder;

const compareText = (left: string, right: string) => left.localeCompare(right, 'ko');

const compareScheduleRows = (left: ScheduleRow, right: ScheduleRow, sortOrder: ScheduleSortOrder) => {
    const { key, direction } = getSortConfig(sortOrder);
    const directionMultiplier = direction === 'asc' ? 1 : -1;
    let result = 0;

    switch (key) {
        case 'type':
            result = compareText(left.typeLabel, right.typeLabel);
            break;
        case 'title':
            result = compareText(left.title, right.title);
            break;
        case 'coach':
            result = compareText(left.coachName || '', right.coachName || '');
            break;
        case 'membership':
            result = compareText(left.membershipName || left.capacityText || '', right.membershipName || right.capacityText || '');
            break;
        case 'status':
            result = STATUS_SORT_WEIGHT[left.status] - STATUS_SORT_WEIGHT[right.status];
            break;
        case 'start':
        default:
            result = left.startTime.valueOf() - right.startTime.valueOf();
            break;
    }

    if (result === 0 && key !== 'start') {
        result = left.startTime.valueOf() - right.startTime.valueOf();
    }

    return result * directionMultiplier;
};

const getUniqueFilterOptions = (rows: ScheduleRow[], getValue: (row: ScheduleRow) => string, getLabel: (row: ScheduleRow) => string) => {
    const optionMap = new Map<string, string>();
    rows.forEach((row) => {
        const value = getValue(row).trim();
        const label = getLabel(row).trim();
        if (!value || !label || optionMap.has(value)) return;
        optionMap.set(value, label);
    });
    return Array.from(optionMap.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ko'));
};

const downloadScheduleRowsAsExcel = (rows: ScheduleRow[], rangeStartDate: string, rangeEndDate: string) => {
    const headers = ['날짜', '시작', '종료', '소요시간', '유형', '수업/대상', '회원/메모', '담당 강사', '수강권', '회차/정원', '상태'];
    const excelRows = rows.map((row) => [
        row.startTime.format('YYYY-MM-DD (ddd)'),
        row.startTime.format('HH:mm'),
        row.endTime.format('HH:mm'),
        `${row.endTime.diff(row.startTime, 'minute')}분`,
        row.typeLabel,
        row.title,
        row.subTitle || '-',
        row.coachName || '-',
        row.membershipName || row.capacityText || '-',
        row.sessionText || '-',
        row.statusLabel,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
    worksheet['!cols'] = [
        { wch: 16 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 10 },
        { wch: 24 },
        { wch: 22 },
        { wch: 14 },
        { wch: 22 },
        { wch: 12 },
        { wch: 10 },
    ];

    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let col = range.s.c; col <= range.e.c; col++) {
        const address = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[address]) continue;
        worksheet[address].s = {
            font: { bold: true, sz: 12 },
            fill: { fgColor: { rgb: 'E8F4FD' } },
            alignment: { horizontal: 'center', vertical: 'center' },
        };
    }

    for (let row = 1; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
            const address = XLSX.utils.encode_cell({ r: row, c: col });
            if (!worksheet[address]) continue;
            worksheet[address].s = {
                ...(worksheet[address].s || {}),
                alignment: { vertical: 'top', wrapText: true },
            };
        }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '스케줄목록');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    saveAs(
        new Blob([excelBuffer], { type: 'application/octet-stream' }),
        `스케줄목록_${rangeStartDate}_${rangeEndDate}.xlsx`
    );
};

const getExportPreviewContent = (
    rows: ScheduleRow[],
    rangeLabel: string,
    sortOrder: ScheduleSortOrder
) => {
    const previewRows = rows.slice(0, 3).map((row, index) => {
        const scheduleLabel = row.subTitle ? `${row.title} / ${row.subTitle}` : row.title;
        return `${index + 1}. ${row.startTime.format('M/D HH:mm')} · ${row.typeLabel} · ${scheduleLabel} · ${row.coachName || '-'} · ${row.statusLabel}`;
    });
    const moreCount = Math.max(0, rows.length - previewRows.length);
    const lines = [
        `현재 화면의 필터와 정렬이 적용된 ${rows.length}건을 엑셀로 다운로드합니다.`,
        '',
        `조회 기간: ${rangeLabel}`,
        `정렬: ${getSortLabel(sortOrder)}`,
        '포함 항목: 날짜, 시작, 종료, 소요시간, 유형, 수업/대상, 회원/메모, 담당 강사, 수강권, 회차/정원, 상태',
        '',
        '미리보기',
        ...previewRows,
    ];

    if (moreCount > 0) {
        lines.push(`외 ${moreCount}건`);
    }

    return lines.join('\n');
};

const ScheduleListView = ({
    viewSwitcher,
    pageType,
    selectedDay,
    dailyDataSource,
    weeklyGridData,
    rangeStartDate,
    rangeEndDate,
    rangeScheduleItems,
    centerId,
    isRangeLoading = false,
    rangeErrorMessage,
    onApplyRange,
    onRetryRange,
    onResetRangeToCurrentView,
    onClickSchedule,
    onClickRequest,
    onClickEtcOffSchedule,
    onClickNewOpportunity,
    onClickApproveScheduleRequest,
    onClickRejectScheduleRequest,
    onClickCreateSchedule,
    createTwoButtonAlert,
}: ScheduleListViewProps) => {
    const [searchText, setSearchText] = useState('');
    const [selectedTypeFilters, setSelectedTypeFilters] = useState<ScheduleRowType[]>(DEFAULT_TYPE_FILTERS);
    const [statusFilter, setStatusFilter] = useState('all');
    const [coachFilter, setCoachFilter] = useState('all');
    const [memberFilter, setMemberFilter] = useState('all');
    const [sortOrder, setSortOrder] = useState<ScheduleSortOrder>(DEFAULT_SORT_ORDER);
    const [savedViews, setSavedViews] = useState<SavedScheduleView[]>([]);
    const [activeSavedViewId, setActiveSavedViewId] = useState('');
    const [savedViewName, setSavedViewName] = useState('');
    const [hasFavoriteCandidate, setHasFavoriteCandidate] = useState(false);
    const [isFavoriteSaveOpen, setIsFavoriteSaveOpen] = useState(false);
    const [draftRangeStartDate, setDraftRangeStartDate] = useState(rangeStartDate);
    const [draftRangeEndDate, setDraftRangeEndDate] = useState(rangeEndDate);
    const hasHydratedFiltersRef = useRef(false);
    const hydratedFilterStorageKeyRef = useRef('');
    const skipNextFilterPersistRef = useRef(false);
    const hasHydratedSavedViewsRef = useRef(false);
    const hydratedSavedViewStorageKeyRef = useRef('');

    const filterStorageKey = useMemo(
        () => getScheduleStorageKey(SCHEDULE_LIST_FILTER_STORAGE_KEY, centerId),
        [centerId]
    );
    const savedViewStorageKey = useMemo(
        () => getScheduleStorageKey(SCHEDULE_LIST_SAVED_VIEW_STORAGE_KEY, centerId),
        [centerId]
    );
    const savedViewMigrationStorageKey = useMemo(
        () => getScheduleStorageKey(SCHEDULE_LIST_SAVED_VIEW_MIGRATION_STORAGE_KEY, centerId),
        [centerId]
    );
    const draftRangeValidation = useMemo(
        () => validateScheduleListRange(draftRangeStartDate, draftRangeEndDate),
        [draftRangeEndDate, draftRangeStartDate]
    );
    const appliedRangeValidation = useMemo(
        () => validateScheduleListRange(rangeStartDate, rangeEndDate),
        [rangeEndDate, rangeStartDate]
    );
    const hasPendingRange =
        draftRangeStartDate !== rangeStartDate || draftRangeEndDate !== rangeEndDate;
    const canRetryAppliedRange = Boolean(rangeErrorMessage) && !hasPendingRange && Boolean(onRetryRange);
    const isLongDraftRange = isLongScheduleListRange(draftRangeStartDate, draftRangeEndDate);

    useEffect(() => {
        setDraftRangeStartDate(rangeStartDate);
        setDraftRangeEndDate(rangeEndDate);
    }, [rangeEndDate, rangeStartDate]);

    const currentFilterSnapshot = useMemo<ScheduleListFilterSnapshot>(
        () => ({
            searchText,
            selectedTypeFilters,
            statusFilter,
            coachFilter,
            memberFilter,
            sortOrder,
            rangeStartDate,
            rangeEndDate,
        }),
        [coachFilter, memberFilter, rangeEndDate, rangeStartDate, searchText, selectedTypeFilters, sortOrder, statusFilter]
    );

    const matchedSavedView = useMemo(
        () => savedViews.find((view) => areFilterSnapshotsEqual(view.filters, currentFilterSnapshot)),
        [currentFilterSnapshot, savedViews]
    );

    const visibleFavoriteId = matchedSavedView?.id || activeSavedViewId;
    const shouldSuggestFavoriteSave = hasFavoriteCandidate && !matchedSavedView && !isFavoriteSaveOpen;

    const markFavoriteCandidate = () => {
        setHasFavoriteCandidate(true);
        setIsFavoriteSaveOpen(false);
        setActiveSavedViewId('');
        setSavedViewName('');
    };

    const stageRange = (startDate: string, endDate: string) => {
        setDraftRangeStartDate(startDate);
        setDraftRangeEndDate(endDate);
    };

    const handleApplyDraftRange = () => {
        if (!draftRangeValidation.isValid || isRangeLoading) return;
        if (canRetryAppliedRange) {
            onRetryRange?.();
            return;
        }
        if (!hasPendingRange) return;
        markFavoriteCandidate();
        onApplyRange(draftRangeStartDate, draftRangeEndDate);
    };

    const applyFilterSnapshot = (filters: ScheduleListFilterSnapshot) => {
        setSearchText(filters.searchText);
        setSelectedTypeFilters(filters.selectedTypeFilters);
        setStatusFilter(filters.statusFilter || 'all');
        setCoachFilter(filters.coachFilter || 'all');
        setMemberFilter(filters.memberFilter || 'all');
        setSortOrder(filters.sortOrder);

        if (
            filters.rangeStartDate &&
            filters.rangeEndDate &&
            validateScheduleListRange(filters.rangeStartDate, filters.rangeEndDate).isValid
        ) {
            onApplyRange(filters.rangeStartDate, filters.rangeEndDate);
        }
    };

    useEffect(() => {
        if (hydratedFilterStorageKeyRef.current === filterStorageKey) return;
        hasHydratedFiltersRef.current = false;
        hydratedFilterStorageKeyRef.current = filterStorageKey;
        const storedFilters = normalizeFilterSnapshot(readStorageJson(filterStorageKey, null));
        if (storedFilters) {
            skipNextFilterPersistRef.current = true;
            applyFilterSnapshot(storedFilters);
        }
        hasHydratedFiltersRef.current = true;
    }, [filterStorageKey]);

    useEffect(() => {
        if (!hasHydratedFiltersRef.current) return;
        if (skipNextFilterPersistRef.current) {
            skipNextFilterPersistRef.current = false;
            return;
        }
        writeStorageJson(filterStorageKey, currentFilterSnapshot);
    }, [currentFilterSnapshot, filterStorageKey]);

    useEffect(() => {
        if (!centerId) return;
        if (hydratedSavedViewStorageKeyRef.current === savedViewStorageKey) return;
        let cancelled = false;
        hasHydratedSavedViewsRef.current = false;
        hydratedSavedViewStorageKeyRef.current = savedViewStorageKey;
        const storedSavedViews = normalizeSavedViews(readStorageJson(savedViewStorageKey, []));
        const fallbackSavedViewStorageKey = getScheduleStorageKey(SCHEDULE_LIST_SAVED_VIEW_STORAGE_KEY);
        const fallbackSavedViews =
            savedViewStorageKey === fallbackSavedViewStorageKey
                ? []
                : normalizeSavedViews(readStorageJson(fallbackSavedViewStorageKey, []));
        const localSavedViews = mergeSavedViews(storedSavedViews, fallbackSavedViews);

        if (storedSavedViews.length === 0 && fallbackSavedViews.length > 0) {
            writeStorageJson(savedViewStorageKey, fallbackSavedViews);
        }

        setSavedViews(localSavedViews);
        setActiveSavedViewId('');
        setSavedViewName('');
        setHasFavoriteCandidate(false);
        setIsFavoriteSaveOpen(false);
        hasHydratedSavedViewsRef.current = true;

        const accessToken = getItemWithExpiration('authorization');
        if (!accessToken) {
            return undefined;
        }

        const syncSavedViews = async () => {
            try {
                const response = await getAdminViewPresetsAPI(centerId, SCHEDULE_LIST_VIEW_TYPE, accessToken);
                const serverSavedViews = (response?.data?.presets || [])
                    .map(normalizeApiSavedView)
                    .filter((view: SavedScheduleView | null): view is SavedScheduleView => Boolean(view));
                const serverFingerprints = new Set(serverSavedViews.map(getSavedViewFingerprint));
                const hasMigratedLocalSavedViews = readStorageJson(savedViewMigrationStorageKey, false);
                const localViewsToMigrate = hasMigratedLocalSavedViews
                    ? []
                    : localSavedViews.filter((view) => !serverFingerprints.has(getSavedViewFingerprint(view)));
                let hasMigrationFailure = false;
                const migratedSavedViews = await Promise.all(
                    localViewsToMigrate.map(async (view) => {
                        try {
                            const createResponse = await createAdminViewPresetAPI(centerId, accessToken, {
                                viewType: SCHEDULE_LIST_VIEW_TYPE,
                                name: view.name,
                                filters: view.filters,
                            });
                            return normalizeApiSavedView(createResponse?.data?.preset) || view;
                        } catch (error) {
                            console.error('Failed to migrate schedule list favorite', error);
                            hasMigrationFailure = true;
                            return view;
                        }
                    })
                );
                const nextSavedViews = hasMigratedLocalSavedViews
                    ? serverSavedViews
                    : mergeSavedViews([...migratedSavedViews, ...serverSavedViews], localSavedViews);
                if (cancelled) return;
                setSavedViews(nextSavedViews);
                writeStorageJson(savedViewStorageKey, nextSavedViews);
                if (!hasMigrationFailure) {
                    writeStorageJson(savedViewMigrationStorageKey, true);
                }
            } catch (error) {
                console.error('Failed to load schedule list favorites', error);
            }
        };

        syncSavedViews();

        return () => {
            cancelled = true;
        };
    }, [centerId, savedViewMigrationStorageKey, savedViewStorageKey]);

    useEffect(() => {
        if (!centerId) return;
        if (!hasHydratedSavedViewsRef.current) return;
        writeStorageJson(savedViewStorageKey, savedViews);
    }, [centerId, savedViewStorageKey, savedViews]);

    const rows = useMemo(() => {
        if (rangeErrorMessage) return [];
        const sourceRows = rangeScheduleItems
            ? flattenScheduleItems(rangeScheduleItems, rangeStartDate, rangeEndDate)
            : pageType === 'daily'
            ? flattenDailyRows(dailyDataSource, selectedDay)
            : flattenWeeklyRows(weeklyGridData);
        return sourceRows.sort((a, b) => a.startTime.valueOf() - b.startTime.valueOf());
    }, [
        dailyDataSource,
        pageType,
        rangeEndDate,
        rangeErrorMessage,
        rangeScheduleItems,
        rangeStartDate,
        selectedDay,
        weeklyGridData,
    ]);

    const coachOptions = useMemo(
        () => getUniqueFilterOptions(rows, (row) => row.coachId || row.coachName, (row) => row.coachName),
        [rows]
    );

    const memberOptions = useMemo(
        () => getUniqueFilterOptions(rows, (row) => row.memberId || row.memberName, (row) => row.memberName),
        [rows]
    );

    useEffect(() => {
        if (isRangeLoading) return;
        if (coachFilter !== 'all' && !coachOptions.some((option) => option.value === coachFilter)) {
            setCoachFilter('all');
        }
    }, [coachFilter, coachOptions, isRangeLoading]);

    useEffect(() => {
        if (isRangeLoading) return;
        if (memberFilter !== 'all' && !memberOptions.some((option) => option.value === memberFilter)) {
            setMemberFilter('all');
        }
    }, [isRangeLoading, memberFilter, memberOptions]);

    const summaryRows = useMemo(() => {
        const keyword = searchText.trim().toLowerCase();
        return rows
            .filter((row) => {
                const matchesSearch = !keyword || row.searchText.includes(keyword);
                const matchesType = selectedTypeFilters.includes(row.type);
                const matchesCoach = coachFilter === 'all' || (row.coachId || row.coachName) === coachFilter;
                const matchesMember = memberFilter === 'all' || (row.memberId || row.memberName) === memberFilter;
                return matchesSearch && matchesType && matchesCoach && matchesMember;
            });
    }, [coachFilter, memberFilter, rows, searchText, selectedTypeFilters]);

    const filteredRows = useMemo(() => {
        return summaryRows
            .filter((row) => {
                return statusFilter === 'all' || row.status === statusFilter;
            })
            .sort((a, b) => compareScheduleRows(a, b, sortOrder));
    }, [sortOrder, statusFilter, summaryRows]);

    const summary = useMemo(
        () => ({
            total: summaryRows.length,
            upcoming: summaryRows.filter((row) => row.status === 'upcoming').length,
            ongoing: summaryRows.filter((row) => row.status === 'ongoing').length,
            completed: summaryRows.filter((row) => row.status === 'completed').length,
            pending: summaryRows.filter((row) => row.status === 'pending').length,
            group: summaryRows.filter((row) => row.type === 'group').length,
        }),
        [summaryRows]
    );

    const handleRowClick = (row: ScheduleRow) => {
        if (row.type === 'request') {
            onClickRequest(row.item);
            return;
        }
        if (row.type === 'onetime' || row.type === 'block') {
            onClickEtcOffSchedule(row.item);
            return;
        }
        if (row.type === 'opportunity') {
            onClickNewOpportunity(row.item);
            return;
        }
        onClickSchedule(row.item);
    };

    const rangeLabel = getRangeLabel(rangeStartDate, rangeEndDate);
    const canExportRows = filteredRows.length > 0 && !isRangeLoading && !rangeErrorMessage;

    const handleDownloadExcel = () => {
        if (!canExportRows) return;
        const downloadExcel = () => downloadScheduleRowsAsExcel(filteredRows, rangeStartDate, rangeEndDate);
        if (!createTwoButtonAlert) {
            downloadExcel();
            return;
        }
        createTwoButtonAlert(
            '스케줄 목록 엑셀 다운로드',
            getExportPreviewContent(filteredRows, rangeLabel, sortOrder),
            false,
            '취소',
            '다운로드',
            undefined,
            downloadExcel
        );
    };

    const handleApplySavedView = (viewId: string) => {
        setActiveSavedViewId(viewId);
        setHasFavoriteCandidate(false);
        setIsFavoriteSaveOpen(false);
        if (!viewId) {
            resetFilters();
            return;
        }
        const savedView = savedViews.find((view) => view.id === viewId);
        if (!savedView) return;
        setSavedViewName(savedView.name);
        applyFilterSnapshot(savedView.filters);
    };

    const getDefaultFavoriteName = () => {
        const coachName = coachOptions.find((option) => option.value === coachFilter)?.label;
        const memberName = memberOptions.find((option) => option.value === memberFilter)?.label;
        const statusName: Record<string, string> = {
            upcoming: '예정',
            ongoing: '진행중',
            completed: '완료',
            pending: '신청 대기',
            noshow: '노쇼',
            off: 'OFF',
        };
        const filterLabels = [coachName, memberName, statusName[statusFilter]].filter(Boolean);
        return filterLabels.length > 0 ? `${filterLabels.join(' · ')} · ${rangeLabel}` : rangeLabel;
    };

    const openFavoriteSave = () => {
        setSavedViewName(getDefaultFavoriteName());
        setIsFavoriteSaveOpen(true);
    };

    const handleSaveCurrentView = () => {
        const trimmedName = savedViewName.trim();
        const nextName = trimmedName || rangeLabel;
        const now = new Date().toISOString();
        const localId = `${LOCAL_SAVED_VIEW_ID_PREFIX}${Date.now()}`;
        const localSavedView: SavedScheduleView = {
            id: localId,
            name: nextName,
            filters: currentFilterSnapshot,
            updatedAt: now,
        };

        setSavedViews((prev) => mergeSavedViews([localSavedView], prev));
        setActiveSavedViewId(localSavedView.id);
        setSavedViewName(nextName);
        setHasFavoriteCandidate(false);
        setIsFavoriteSaveOpen(false);

        const accessToken = getItemWithExpiration('authorization');
        if (!centerId || !accessToken) return;

        createAdminViewPresetAPI(centerId, accessToken, {
            viewType: SCHEDULE_LIST_VIEW_TYPE,
            name: nextName,
            filters: currentFilterSnapshot,
        })
            .then((response) => {
                const serverSavedView = normalizeApiSavedView(response?.data?.preset);
                if (!serverSavedView) return;
                setSavedViews((prev) =>
                    mergeSavedViews(
                        [serverSavedView],
                        prev.filter((view) => view.id !== localId)
                    )
                );
                setActiveSavedViewId(serverSavedView.id);
            })
            .catch((error) => {
                console.error('Failed to save schedule list favorite', error);
            });
    };

    const handleDeleteSavedView = () => {
        if (!visibleFavoriteId) return;
        const targetSavedViewId = visibleFavoriteId;
        const targetSavedView = savedViews.find((view) => view.id === visibleFavoriteId);
        const deleteSavedView = async () => {
            setSavedViews((prev) => prev.filter((view) => view.id !== targetSavedViewId));
            setActiveSavedViewId('');
            setSavedViewName('');
            setHasFavoriteCandidate(true);
            setIsFavoriteSaveOpen(false);

            const accessToken = getItemWithExpiration('authorization');
            if (!targetSavedView || !centerId || !accessToken || isLocalSavedViewId(targetSavedViewId)) return;

            try {
                await deleteAdminViewPresetAPI(centerId, targetSavedViewId, accessToken);
            } catch (error) {
                console.error('Failed to delete schedule list favorite', error);
                setSavedViews((prev) => mergeSavedViews([targetSavedView], prev));
            }
        };

        if (!createTwoButtonAlert) {
            if (window.confirm('즐겨찾기를 삭제하시겠습니까?')) {
                deleteSavedView();
            }
            return;
        }

        createTwoButtonAlert(
            '즐겨찾기 삭제',
            `${targetSavedView?.name || '선택한 즐겨찾기'}를 삭제하시겠습니까?`,
            false,
            '취소',
            '삭제',
            undefined,
            deleteSavedView
        );
    };

    const toggleTypeFilter = (type: ScheduleRowType) => {
        markFavoriteCandidate();
        setSelectedTypeFilters((prev) =>
            prev.includes(type) ? prev.filter((selectedType) => selectedType !== type) : [...prev, type]
        );
    };

    const resetFilters = () => {
        setSearchText('');
        setSelectedTypeFilters(DEFAULT_TYPE_FILTERS);
        setStatusFilter('all');
        setCoachFilter('all');
        setMemberFilter('all');
        setSortOrder(DEFAULT_SORT_ORDER);
        onResetRangeToCurrentView();
        setActiveSavedViewId('');
        setSavedViewName('');
        setHasFavoriteCandidate(false);
        setIsFavoriteSaveOpen(false);
    };

    const sortConfig = getSortConfig(sortOrder);
    const currentViewStartDate =
        pageType === 'weekly'
            ? startOfLegacyMondayWeek(selectedDay).format('YYYY-MM-DD')
            : dayjs(selectedDay).format('YYYY-MM-DD');
    const currentViewEndDate =
        pageType === 'weekly'
            ? endOfLegacyMondayWeek(selectedDay).format('YYYY-MM-DD')
            : dayjs(selectedDay).format('YYYY-MM-DD');
    const today = dayjs().startOf('day');
    const dateRangePresets = [
        {
            label: '현재 보기',
            startDate: currentViewStartDate,
            endDate: currentViewEndDate,
        },
        {
            label: '지난주',
            startDate: startOfLegacyMondayWeek(today.subtract(1, 'week')).format('YYYY-MM-DD'),
            endDate: endOfLegacyMondayWeek(today.subtract(1, 'week')).format('YYYY-MM-DD'),
        },
        {
            label: '이번주',
            startDate: startOfLegacyMondayWeek(today).format('YYYY-MM-DD'),
            endDate: endOfLegacyMondayWeek(today).format('YYYY-MM-DD'),
        },
        {
            label: '다음주',
            startDate: startOfLegacyMondayWeek(today.add(1, 'week')).format('YYYY-MM-DD'),
            endDate: endOfLegacyMondayWeek(today.add(1, 'week')).format('YYYY-MM-DD'),
        },
        {
            label: '앞으로 30일',
            startDate: today.format('YYYY-MM-DD'),
            endDate: today.add(30, 'day').format('YYYY-MM-DD'),
        },
        {
            label: '3개월',
            startDate: today.format('YYYY-MM-DD'),
            endDate: today.add(3, 'month').format('YYYY-MM-DD'),
        },
        {
            label: '6개월',
            startDate: today.format('YYYY-MM-DD'),
            endDate: today.add(6, 'month').format('YYYY-MM-DD'),
        },
        {
            label: '1년',
            startDate: today.format('YYYY-MM-DD'),
            endDate: today.add(1, 'year').format('YYYY-MM-DD'),
        },
    ];
    const draftMaxEndDate = getScheduleListMaxEndDate(draftRangeStartDate);
    const rangeFeedbackTone: RangeFeedbackTone = !draftRangeValidation.isValid
        ? 'error'
        : hasPendingRange
        ? isLongDraftRange
            ? 'warning'
            : 'pending'
        : isRangeLoading
        ? 'info'
        : rangeErrorMessage
        ? 'error'
        : isLongDraftRange
        ? 'warning'
        : 'info';
    const rangeFeedbackMessage = !draftRangeValidation.isValid
        ? draftRangeValidation.errorMessage
        : hasPendingRange
        ? `아직 적용되지 않은 기간입니다. ‘기간 적용’을 눌러 조회하세요.${
              isLongDraftRange ? ' 6개월 이상은 데이터 양에 따라 수초 이상 걸릴 수 있습니다.' : ''
          }`
        : isRangeLoading
        ? '선택한 기간의 일정을 불러오고 있습니다.'
        : rangeErrorMessage
        ? rangeErrorMessage
        : isLongDraftRange
        ? '6개월 이상 조회는 데이터 양에 따라 수초 이상 걸릴 수 있습니다.'
        : '최대 1년, 시작일 기준 다음 해 같은 날짜까지 조회할 수 있습니다.';
    const isCurrentViewRange =
        dayjs(rangeStartDate).isSame(currentViewStartDate, 'day') &&
        dayjs(rangeEndDate).isSame(currentViewEndDate, 'day');
    const isDefaultTypeFilter = areTypeFiltersEqual(selectedTypeFilters, DEFAULT_TYPE_FILTERS);
    const selectedTypeFilterLabel = selectedTypeFilters
        .map((type) => TYPE_FILTER_OPTIONS.find((option) => option.value === type)?.label)
        .filter(Boolean)
        .join(', ');
    const selectedCoachLabel = coachOptions.find((option) => option.value === coachFilter)?.label;
    const selectedMemberLabel = memberOptions.find((option) => option.value === memberFilter)?.label;
    const selectedStatusLabel = STATUS_FILTER_LABELS[statusFilter];
    const hasActiveFilters =
        Boolean(searchText.trim()) ||
        !isDefaultTypeFilter ||
        statusFilter !== 'all' ||
        coachFilter !== 'all' ||
        memberFilter !== 'all' ||
        sortOrder !== DEFAULT_SORT_ORDER ||
        !isCurrentViewRange;

    const handleSortHeaderClick = (key: ScheduleSortKey) => {
        markFavoriteCandidate();
        setSortOrder((prev) => {
            const currentSortConfig = getSortConfig(prev);
            const nextDirection = currentSortConfig.key === key && currentSortConfig.direction === 'asc' ? 'desc' : 'asc';
            return toSortOrder(key, nextDirection);
        });
    };

    const renderSortHeader = (label: string, key: ScheduleSortKey, center = false) => {
        const active = sortConfig.key === key;
        return (
            <SortHeaderButton
                type="button"
                $active={active}
                $center={center}
                onClick={() => handleSortHeaderClick(key)}
                title={`${label} 기준으로 정렬`}
            >
                {label}
                <SortIndicator $active={active}>{active ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '↕'}</SortIndicator>
            </SortHeaderButton>
        );
    };

    const handleRowKeyDown = (event: React.KeyboardEvent, row: ScheduleRow) => {
        if (event.target !== event.currentTarget) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        handleRowClick(row);
    };

    return (
        <PageWrap>
            <Toolbar>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
                    <div
                        style={{
                            width: 44,
                            height: 44,
                            borderRadius: 12,
                            background: '#EEF4FF',
                            color: colors.BLUE_500,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flex: '0 0 44px',
                        }}
                    >
                        <Clock3 size={22} strokeWidth={2.2} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                        <div style={{ color: '#17264a', fontSize: 22, fontWeight: 900, lineHeight: '28px' }}>
                            스케줄 목록
                        </div>
                        <div style={{ color: '#8490a3', fontSize: 13, fontWeight: 700, marginTop: 4 }}>
                            {rangeLabel} · 시간순 정렬
                        </div>
                    </div>
                </div>
                <ToolbarActions>
                    <SecondaryActionButton
                        type="button"
                        onClick={handleDownloadExcel}
                        disabled={!canExportRows}
                        title={canExportRows ? '현재 필터 결과를 엑셀로 다운로드' : '내보낼 스케줄이 없습니다'}
                    >
                        <Download size={16} strokeWidth={2.3} />
                        엑셀
                    </SecondaryActionButton>
                    <CreateButton type="button" onClick={onClickCreateSchedule}>
                        <CalendarPlus size={18} strokeWidth={2.4} />
                        일정 생성
                    </CreateButton>
                    {viewSwitcher}
                </ToolbarActions>
            </Toolbar>

            <SummaryGrid>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>전체</span>
                    <span style={{ color: '#17264a', fontSize: 24, fontWeight: 900 }}>{summary.total}건</span>
                </SummaryCard>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>예정</span>
                    <span style={{ color: '#1D4ED8', fontSize: 24, fontWeight: 900 }}>{summary.upcoming}건</span>
                </SummaryCard>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>진행중</span>
                    <span style={{ color: '#15803D', fontSize: 24, fontWeight: 900 }}>{summary.ongoing}건</span>
                </SummaryCard>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>완료</span>
                    <span style={{ color: '#475569', fontSize: 24, fontWeight: 900 }}>{summary.completed}건</span>
                </SummaryCard>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>신청 대기</span>
                    <span style={{ color: '#C2410C', fontSize: 24, fontWeight: 900 }}>{summary.pending}건</span>
                </SummaryCard>
                <SummaryCard>
                    <span style={{ color: '#8490a3', fontSize: 12, fontWeight: 800 }}>그룹/듀엣</span>
                    <span style={{ color: '#065F46', fontSize: 24, fontWeight: 900 }}>{summary.group}건</span>
                </SummaryCard>
            </SummaryGrid>

            <FilterRow>
                <FilterTopRow>
                    <SearchBox>
                        <Search size={18} strokeWidth={2.2} />
                        <SearchInput
                            value={searchText}
                            onChange={(event) => {
                                markFavoriteCandidate();
                                setSearchText(event.target.value);
                            }}
                            placeholder="수업명, 회원명, 강사명 검색"
                        />
                    </SearchBox>
                    <TopRightControls>
                        {savedViews.length > 0 && (
                            <FavoriteControls>
                                <FavoriteSelect
                                    value={visibleFavoriteId}
                                    onChange={(event) => handleApplySavedView(event.target.value)}
                                >
                                    <option value="">즐겨찾기 선택</option>
                                    {savedViews.map((view) => (
                                        <option key={view.id} value={view.id}>
                                            {view.name}
                                        </option>
                                    ))}
                                </FavoriteSelect>
                                {visibleFavoriteId && (
                                    <FavoriteIconButton
                                        type="button"
                                        onClick={handleDeleteSavedView}
                                        title="즐겨찾기 삭제"
                                    >
                                        <Trash2 size={14} strokeWidth={2.3} />
                                    </FavoriteIconButton>
                                )}
                            </FavoriteControls>
                        )}
                        <CountInfo>
                            <Filter size={16} strokeWidth={2.2} />
                            {rangeErrorMessage || (isRangeLoading ? '불러오는 중' : `현재 표시 ${filteredRows.length}건`)}
                        </CountInfo>
                    </TopRightControls>
                </FilterTopRow>

                <DateRangePanel aria-labelledby="schedule-range-title">
                    <DateRangeHeader>
                        <DateRangeTitleGroup>
                            <CalendarRange size={19} strokeWidth={2.3} aria-hidden="true" />
                            <div>
                                <DateRangeTitle id="schedule-range-title">조회 기간</DateRangeTitle>
                                <DateRangeSubtitle>
                                    날짜를 편집한 뒤 ‘기간 적용’을 눌러야 조회됩니다. 입력 중에는 서버에 요청하지 않습니다.
                                </DateRangeSubtitle>
                            </div>
                        </DateRangeTitleGroup>
                        <AppliedRangeBadge title="현재 서버에서 조회한 기간">
                            <CheckCircle2 size={14} strokeWidth={2.5} aria-hidden="true" />
                            적용 중 · {rangeLabel} · {appliedRangeValidation.dayCount || 0}일
                        </AppliedRangeBadge>
                    </DateRangeHeader>

                    <DateRangeControls>
                        <WeekPresetGroup aria-label="조회 기간 빠른 선택">
                            {dateRangePresets.map((preset) => (
                                <RangeButton
                                    key={preset.label}
                                    type="button"
                                    $selected={isSameRange(
                                        draftRangeStartDate,
                                        draftRangeEndDate,
                                        preset.startDate,
                                        preset.endDate
                                    )}
                                    onClick={() => stageRange(preset.startDate, preset.endDate)}
                                    aria-pressed={isSameRange(
                                        draftRangeStartDate,
                                        draftRangeEndDate,
                                        preset.startDate,
                                        preset.endDate
                                    )}
                                >
                                    {preset.label}
                                </RangeButton>
                            ))}
                        </WeekPresetGroup>

                        <DateEditor>
                            <DateField>
                                <DateFieldLabel>시작일</DateFieldLabel>
                                <DateInput
                                    type="date"
                                    value={draftRangeStartDate}
                                    min={SCHEDULE_LIST_MIN_DATE}
                                    max={SCHEDULE_LIST_MAX_DATE}
                                    $invalid={!draftRangeValidation.isValid}
                                    aria-invalid={!draftRangeValidation.isValid}
                                    aria-describedby="schedule-range-feedback"
                                    onChange={(event) => setDraftRangeStartDate(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') handleApplyDraftRange();
                                    }}
                                />
                            </DateField>
                            <ArrowRight
                                size={17}
                                strokeWidth={2.3}
                                color="#8a96aa"
                                aria-hidden="true"
                                style={{ marginBottom: 10 }}
                            />
                            <DateField>
                                <DateFieldLabel>종료일</DateFieldLabel>
                                <DateInput
                                    type="date"
                                    value={draftRangeEndDate}
                                    min={draftRangeStartDate || SCHEDULE_LIST_MIN_DATE}
                                    max={draftMaxEndDate}
                                    $invalid={!draftRangeValidation.isValid}
                                    aria-invalid={!draftRangeValidation.isValid}
                                    aria-describedby="schedule-range-feedback"
                                    onChange={(event) => setDraftRangeEndDate(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') handleApplyDraftRange();
                                    }}
                                />
                            </DateField>
                            <ApplyRangeButton
                                type="button"
                                onClick={handleApplyDraftRange}
                                disabled={
                                    !draftRangeValidation.isValid ||
                                    (!hasPendingRange && !canRetryAppliedRange) ||
                                    isRangeLoading
                                }
                                aria-describedby="schedule-range-feedback"
                            >
                                <Search size={15} strokeWidth={2.5} aria-hidden="true" />
                                {isRangeLoading ? '조회 중' : canRetryAppliedRange ? '다시 조회' : '기간 적용'}
                            </ApplyRangeButton>
                        </DateEditor>
                    </DateRangeControls>

                    <RangeFeedback
                        id="schedule-range-feedback"
                        $tone={rangeFeedbackTone}
                        role={rangeFeedbackTone === 'error' ? 'alert' : 'status'}
                        aria-live={rangeFeedbackTone === 'error' ? 'assertive' : 'polite'}
                    >
                        {rangeFeedbackTone === 'error' || rangeFeedbackTone === 'warning' ? (
                            <AlertTriangle size={15} strokeWidth={2.4} aria-hidden="true" />
                        ) : rangeFeedbackTone === 'pending' ? (
                            <Clock3 size={15} strokeWidth={2.4} aria-hidden="true" />
                        ) : (
                            <Info size={15} strokeWidth={2.4} aria-hidden="true" />
                        )}
                        {rangeFeedbackMessage}
                    </RangeFeedback>
                </DateRangePanel>

                {hasActiveFilters && (
                    <ActiveFilterBar>
                        {!isCurrentViewRange && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    onResetRangeToCurrentView();
                                }}
                            >
                                기간: {rangeLabel} ×
                            </ActiveFilterChip>
                        )}
                        {searchText.trim() && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setSearchText('');
                                }}
                            >
                                검색: {searchText.trim()} ×
                            </ActiveFilterChip>
                        )}
                        {!isDefaultTypeFilter && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setSelectedTypeFilters(DEFAULT_TYPE_FILTERS);
                                }}
                            >
                                유형: {selectedTypeFilterLabel || '없음'} ×
                            </ActiveFilterChip>
                        )}
                        {statusFilter !== 'all' && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setStatusFilter('all');
                                }}
                            >
                                상태: {selectedStatusLabel} ×
                            </ActiveFilterChip>
                        )}
                        {coachFilter !== 'all' && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setCoachFilter('all');
                                }}
                            >
                                강사: {selectedCoachLabel || '선택됨'} ×
                            </ActiveFilterChip>
                        )}
                        {memberFilter !== 'all' && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setMemberFilter('all');
                                }}
                            >
                                회원: {selectedMemberLabel || '선택됨'} ×
                            </ActiveFilterChip>
                        )}
                        {sortOrder !== DEFAULT_SORT_ORDER && (
                            <ActiveFilterChip
                                type="button"
                                onClick={() => {
                                    markFavoriteCandidate();
                                    setSortOrder(DEFAULT_SORT_ORDER);
                                }}
                            >
                                정렬: {getSortLabel(sortOrder)} ×
                            </ActiveFilterChip>
                        )}
                    </ActiveFilterBar>
                )}

                {(shouldSuggestFavoriteSave || isFavoriteSaveOpen) && (
                    <FavoriteBar>
                        {shouldSuggestFavoriteSave && (
                            <FavoritePrompt>
                                현재 필터 조건을 즐겨찾기로 저장할 수 있습니다.
                                <FavoritePrimaryButton type="button" onClick={openFavoriteSave}>
                                    <Save size={14} strokeWidth={2.3} />
                                    저장
                                </FavoritePrimaryButton>
                            </FavoritePrompt>
                        )}

                        {isFavoriteSaveOpen && (
                            <FavoriteControls>
                                <FavoriteNameInput
                                    value={savedViewName}
                                    onChange={(event) => setSavedViewName(event.target.value)}
                                    placeholder="즐겨찾기 이름"
                                />
                                <FavoritePrimaryButton type="button" onClick={handleSaveCurrentView}>
                                    저장
                                </FavoritePrimaryButton>
                                <FavoriteButton
                                    type="button"
                                    onClick={() => {
                                        setIsFavoriteSaveOpen(false);
                                    }}
                                >
                                    취소
                                </FavoriteButton>
                            </FavoriteControls>
                        )}
                    </FavoriteBar>
                )}

                <FilterBottomRow>
                    <FilterGroup>
                        <FilterLabel>유형</FilterLabel>
                        <TypeFilterGroup aria-label="스케줄 유형 필터">
                            {TYPE_FILTER_OPTIONS.map((option) => {
                                const selected = selectedTypeFilters.includes(option.value);
                                return (
                                    <TypeFilterButton
                                        key={option.value}
                                        type="button"
                                        $selected={selected}
                                        aria-pressed={selected}
                                        onClick={() => toggleTypeFilter(option.value)}
                                    >
                                        {option.label}
                                    </TypeFilterButton>
                                );
                            })}
                        </TypeFilterGroup>
                    </FilterGroup>

                    <SelectGroup>
                        <SelectControl>
                            <FilterLabel>상태</FilterLabel>
                            <Select
                                value={statusFilter}
                                onChange={(event) => {
                                    markFavoriteCandidate();
                                    setStatusFilter(event.target.value);
                                }}
                            >
                                <option value="all">전체 상태</option>
                                <option value="upcoming">예정</option>
                                <option value="ongoing">진행중</option>
                                <option value="completed">완료</option>
                                <option value="pending">신청 대기</option>
                                <option value="noshow">노쇼</option>
                                <option value="off">OFF</option>
                            </Select>
                        </SelectControl>
                        <SelectControl>
                            <FilterLabel>강사</FilterLabel>
                            <Select
                                value={coachFilter}
                                onChange={(event) => {
                                    markFavoriteCandidate();
                                    setCoachFilter(event.target.value);
                                }}
                            >
                                <option value="all">전체 강사</option>
                                {coachOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </SelectControl>
                        <SelectControl>
                            <FilterLabel>회원</FilterLabel>
                            <Select
                                value={memberFilter}
                                onChange={(event) => {
                                    markFavoriteCandidate();
                                    setMemberFilter(event.target.value);
                                }}
                            >
                                <option value="all">전체 회원</option>
                                {memberOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </SelectControl>
                        <SelectControl>
                            <FilterLabel>정렬</FilterLabel>
                            <Select
                                value={sortOrder}
                                onChange={(event) => {
                                    markFavoriteCandidate();
                                    setSortOrder(event.target.value as ScheduleSortOrder);
                                }}
                            >
                                {SORT_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </SelectControl>
                        <ResetFilterButton type="button" $active={hasActiveFilters} onClick={resetFilters}>
                            <RotateCcw size={14} strokeWidth={2.2} />
                            초기화
                        </ResetFilterButton>
                    </SelectGroup>
                </FilterBottomRow>
            </FilterRow>

            <TableShell>
                <TableScroll>
                    <GridRow $header>
                        <HeaderLabel>날짜</HeaderLabel>
                        {renderSortHeader('시간', 'start')}
                        <CenterCell>{renderSortHeader('유형', 'type', true)}</CenterCell>
                        {renderSortHeader('수업 / 대상', 'title')}
                        {renderSortHeader('담당 강사', 'coach')}
                        {renderSortHeader('수강권 / 회차', 'membership')}
                        <CenterCell>{renderSortHeader('상태', 'status', true)}</CenterCell>
                        <CenterCell>관리</CenterCell>
                    </GridRow>

                    {isRangeLoading ? (
                        <EmptyState>
                            <Clock3 size={28} strokeWidth={2.2} />
                            스케줄을 불러오는 중입니다
                        </EmptyState>
                    ) : rangeErrorMessage ? (
                        <EmptyState>
                            <Filter size={28} strokeWidth={2.2} />
                            {rangeErrorMessage}
                        </EmptyState>
                    ) : filteredRows.length === 0 ? (
                        <EmptyState>
                            <CheckCircle2 size={28} strokeWidth={2.2} />
                            조건에 맞는 스케줄이 없습니다
                        </EmptyState>
                    ) : (
                        filteredRows.map((row) => (
                            <GridRow
                                key={row.key}
                                role="button"
                                tabIndex={0}
                                title="상세 보기"
                                onClick={() => handleRowClick(row)}
                                onKeyDown={(event) => handleRowKeyDown(event, row)}
                            >
                                <CellText>{row.startTime.format('M월 D일 (ddd)')}</CellText>
                                <CellText>
                                    {row.startTime.format('HH:mm')} ~ {row.endTime.format('HH:mm')}
                                    <SubText>{row.endTime.diff(row.startTime, 'minute')}분</SubText>
                                </CellText>
                                <CenterCell>
                                    <TypeBadge $tone={row.typeTone}>{row.typeLabel}</TypeBadge>
                                </CenterCell>
                                <div style={{ minWidth: 0 }}>
                                    <CellText>{row.title}</CellText>
                                    {row.subTitle && <SubText>{row.subTitle}</SubText>}
                                </div>
                                <CellText>{row.coachName ? `${row.coachName} 강사` : '-'}</CellText>
                                <div style={{ minWidth: 0 }}>
                                    <CellText>{row.membershipName || row.capacityText || '-'}</CellText>
                                    {row.sessionText && (
                                        <SubText style={{ color: row.type === 'group' ? '#059669' : colors.BLUE_500 }}>
                                            {row.type === 'group' ? (
                                                <>
                                                    <UsersRound size={13} strokeWidth={2.4} style={{ verticalAlign: -2, marginRight: 4 }} />
                                                    {row.sessionText}
                                                </>
                                            ) : (
                                                row.sessionText
                                            )}
                                        </SubText>
                                    )}
                                </div>
                                <CenterCell>
                                    <StatusBadge $status={row.status}>{row.statusLabel}</StatusBadge>
                                </CenterCell>
                                <ActionCell>
                                    {(row.type === 'private' || row.type === 'onetime') && <QuickMoveRawEntry item={row.item} compact />}
                                    {row.type === 'request' ? (
                                        <>
                                            <InlineButton
                                                type="button"
                                                $variant="primary"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onClickApproveScheduleRequest(row.item);
                                                }}
                                            >
                                                승인
                                            </InlineButton>
                                            <InlineButton
                                                type="button"
                                                $variant="danger"
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onClickRejectScheduleRequest(row.item);
                                                }}
                                            >
                                                거절
                                            </InlineButton>
                                        </>
                                    ) : (
                                        <InlineButton
                                            type="button"
                                            $variant="primary"
                                            onClick={(event) => {
                                                event.stopPropagation();
                                                handleRowClick(row);
                                            }}
                                        >
                                            상세
                                        </InlineButton>
                                    )}
                                </ActionCell>
                            </GridRow>
                        ))
                    )}
                </TableScroll>
            </TableShell>
        </PageWrap>
    );
};

export default ScheduleListView;
